/**
 * 文档调整参数 → 可执行形式（M2-15）。
 *
 * 输入是结构化鸭子类型（不 import document——分层），字段约定：
 * - curves: params.points = [x0,y0,x1,y1,…]（平铺的控制点）
 * - levels: params.inBlack/inWhite/gamma/outBlack/outWhite
 * - hsl:    params.hue/saturation/lightness
 * - lut:    params.size（边长）/params.data（size³×3 展开，[0,1]）/
 *           params.domainMin/params.domainMax（各 3 项，默认 [0,0,0]/[1,1,1]）
 * - basic:  params.exposure/highlights/shadows/whites/blacks/clarity/dehaze
 *           （M4-08，七个都缺省 0）
 * - blackAndWhite/photoFilter/channelMixer/gradientMap/posterize/threshold/
 *   vibrance/colorBalance/brightnessContrast：见对比 Photoshop 补全第一档
 *   缺口 阶段 1；字段形状见各自的 engine/adjustments/<kind>.ts 模块
 * 未知种类、或已知种类但字段形状不对，一律返回 null，调用方跳过
 * （新种类先落格式再落实现；畸形的已知种类当作没有这个调整，不炸合成）。
 */
import { adjustHsl, curvesLut, levelsLut, type HslParams } from './lut';
import { sampleLut3d, type Lut3D } from './lut3d';
import { basicToneLut, type BasicParams } from './basic';
import { applyBlackAndWhite, type BlackAndWhiteParams } from './blackwhite';
import { applyPhotoFilter, type PhotoFilterParams } from './photofilter';
import { applyChannelMixer, type ChannelMixerParams } from './channelmixer';
import { applyGradientMap, type GradientMapParams } from './gradientmap';
import { posterizeLut } from './posterize';
import { applyThreshold, type ThresholdParams } from './threshold';
import { applyVibrance, type VibranceParams } from './vibrance';
import { applyColorBalance, type ColorBalanceParams } from './colorbalance';
import { brightnessContrastLut } from './brightnesscontrast';

export interface AdjustmentLike {
  readonly kind: string;
  readonly params: Readonly<Record<string, number | readonly number[]>>;
}

export type ResolvedAdjustment =
  | { readonly type: 'lut'; readonly lut: Uint8Array }
  | { readonly type: 'hsl'; readonly params: HslParams }
  | { readonly type: 'lut3d'; readonly lut: Lut3D }
  | {
      readonly type: 'basic';
      readonly lut: Uint8Array;
      readonly clarity: number;
      readonly dehaze: number;
    }
  | { readonly type: 'blackAndWhite'; readonly params: BlackAndWhiteParams }
  | { readonly type: 'photoFilter'; readonly params: PhotoFilterParams }
  | { readonly type: 'channelMixer'; readonly params: ChannelMixerParams }
  | { readonly type: 'gradientMap'; readonly params: GradientMapParams }
  | { readonly type: 'threshold'; readonly params: ThresholdParams }
  | { readonly type: 'vibrance'; readonly params: VibranceParams }
  | { readonly type: 'colorBalance'; readonly params: ColorBalanceParams };

export function resolveAdjustment(adjustment: AdjustmentLike): ResolvedAdjustment | null {
  const params = adjustment.params;
  switch (adjustment.kind) {
    case 'curves': {
      const flat = params['points'];
      if (!Array.isArray(flat)) return null;
      const points = [];
      for (let i = 0; i + 1 < flat.length; i += 2) {
        points.push({ x: flat[i]!, y: flat[i + 1]! });
      }
      return { type: 'lut', lut: curvesLut(points) };
    }
    case 'levels': {
      const num = (key: string, fallback: number): number => {
        const v = params[key];
        return typeof v === 'number' ? v : fallback;
      };
      return {
        type: 'lut',
        lut: levelsLut({
          inBlack: num('inBlack', 0),
          inWhite: num('inWhite', 255),
          gamma: num('gamma', 1),
          outBlack: num('outBlack', 0),
          outWhite: num('outWhite', 255),
        }),
      };
    }
    case 'hsl': {
      const num = (key: string): number => {
        const v = params[key];
        return typeof v === 'number' ? v : 0;
      };
      return {
        type: 'hsl',
        params: { hue: num('hue'), saturation: num('saturation'), lightness: num('lightness') },
      };
    }
    case 'lut': {
      const size = params['size'];
      const data = params['data'];
      if (typeof size !== 'number' || !Number.isInteger(size) || size < 2) return null;
      if (!Array.isArray(data) || data.length !== size * size * size * 3) return null;
      const domainMin = tripleOrDefault(params['domainMin'], [0, 0, 0]);
      const domainMax = tripleOrDefault(params['domainMax'], [1, 1, 1]);
      if (domainMin === null || domainMax === null) return null;
      return {
        type: 'lut3d',
        lut: { size, data: Float32Array.from(data), domainMin, domainMax },
      };
    }
    case 'basic': {
      const num = (key: keyof BasicParams): number => {
        const v = params[key];
        return typeof v === 'number' ? v : 0;
      };
      const basicParams: BasicParams = {
        exposure: num('exposure'),
        highlights: num('highlights'),
        shadows: num('shadows'),
        whites: num('whites'),
        blacks: num('blacks'),
        clarity: num('clarity'),
        dehaze: num('dehaze'),
      };
      return {
        type: 'basic',
        lut: basicToneLut(basicParams),
        clarity: basicParams.clarity,
        dehaze: basicParams.dehaze,
      };
    }
    case 'posterize': {
      const levels = params['levels'];
      return { type: 'lut', lut: posterizeLut(typeof levels === 'number' ? levels : 256) };
    }
    case 'brightnessContrast': {
      const num = (key: string): number => {
        const v = params[key];
        return typeof v === 'number' ? v : 0;
      };
      return {
        type: 'lut',
        lut: brightnessContrastLut({ brightness: num('brightness'), contrast: num('contrast') }),
      };
    }
    case 'blackAndWhite': {
      const num = (key: string, fallback: number): number => {
        const v = params[key];
        return typeof v === 'number' ? v : fallback;
      };
      return {
        type: 'blackAndWhite',
        params: { r: num('r', 40), g: num('g', 40), b: num('b', 20) },
      };
    }
    case 'photoFilter': {
      const num = (key: string, fallback: number): number => {
        const v = params[key];
        return typeof v === 'number' ? v : fallback;
      };
      return {
        type: 'photoFilter',
        params: {
          r: num('r', 255),
          g: num('g', 157),
          b: num('b', 0),
          density: num('density', 0),
          preserveLuminosity: num('preserveLuminosity', 1),
        },
      };
    }
    case 'channelMixer': {
      const num = (key: string, fallback: number): number => {
        const v = params[key];
        return typeof v === 'number' ? v : fallback;
      };
      return {
        type: 'channelMixer',
        params: {
          rr: num('rr', 100),
          rg: num('rg', 0),
          rb: num('rb', 0),
          rc: num('rc', 0),
          gr: num('gr', 0),
          gg: num('gg', 100),
          gb: num('gb', 0),
          gc: num('gc', 0),
          br: num('br', 0),
          bg: num('bg', 0),
          bb: num('bb', 100),
          bc: num('bc', 0),
        },
      };
    }
    case 'gradientMap': {
      const color1 = tripleOrDefault(params['color1'], [0, 0, 0]);
      const color2 = tripleOrDefault(params['color2'], [255, 255, 255]);
      if (color1 === null || color2 === null) return null;
      return { type: 'gradientMap', params: { color1, color2 } };
    }
    case 'threshold': {
      const level = params['level'];
      return {
        type: 'threshold',
        params: { level: typeof level === 'number' ? level : 128 },
      };
    }
    case 'vibrance': {
      const amount = params['amount'];
      return {
        type: 'vibrance',
        params: { amount: typeof amount === 'number' ? amount : 0 },
      };
    }
    case 'colorBalance': {
      const num = (key: string): number => {
        const v = params[key];
        return typeof v === 'number' ? v : 0;
      };
      const preserveLuminosity = params['preserveLuminosity'];
      return {
        type: 'colorBalance',
        params: {
          shadowR: num('shadowR'),
          shadowG: num('shadowG'),
          shadowB: num('shadowB'),
          midR: num('midR'),
          midG: num('midG'),
          midB: num('midB'),
          highR: num('highR'),
          highG: num('highG'),
          highB: num('highB'),
          preserveLuminosity: typeof preserveLuminosity === 'number' ? preserveLuminosity : 1,
        },
      };
    }
    default:
      return null;
  }
}

function tripleOrDefault(
  value: number | readonly number[] | undefined,
  fallback: readonly [number, number, number],
): [number, number, number] | null {
  if (value === undefined) return [...fallback];
  if (!Array.isArray(value) || value.length !== 3) return null;
  return [value[0]!, value[1]!, value[2]!];
}

/**
 * 对单个 RGB 应用已解析的调整。
 *
 * `basic` 只在这里应用它的逐通道 LUT 部分（曝光/高光/阴影/白色/黑色）——
 * 清晰度/去朦胧要看邻域，逐像素函数做不了，由调用方（`app/composite.ts`
 * 的 `compositeAdjustment`）在整个合成区域上单独跑一遍空间滤波。
 */
export function applyResolved(
  resolved: ResolvedAdjustment,
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  if (resolved.type === 'lut' || resolved.type === 'basic') {
    return [resolved.lut[r]!, resolved.lut[g]!, resolved.lut[b]!];
  }
  if (resolved.type === 'lut3d') {
    return sampleLut3d(resolved.lut, r, g, b);
  }
  if (resolved.type === 'hsl') {
    return adjustHsl(r, g, b, resolved.params);
  }
  if (resolved.type === 'blackAndWhite') {
    return applyBlackAndWhite(r, g, b, resolved.params);
  }
  if (resolved.type === 'photoFilter') {
    return applyPhotoFilter(r, g, b, resolved.params);
  }
  if (resolved.type === 'channelMixer') {
    return applyChannelMixer(r, g, b, resolved.params);
  }
  if (resolved.type === 'gradientMap') {
    return applyGradientMap(r, g, b, resolved.params);
  }
  if (resolved.type === 'threshold') {
    return applyThreshold(r, g, b, resolved.params);
  }
  if (resolved.type === 'vibrance') {
    return applyVibrance(r, g, b, resolved.params);
  }
  return applyColorBalance(r, g, b, resolved.params);
}
